import { useCallback, useEffect, useRef, useState } from 'react';

interface UseDonateItemDropdownReturn<T> {
	isOpen: boolean;
	filterText: string;
	filteredOptions: T[];
	containerRef: React.RefObject<HTMLDivElement>;
	inputRef: React.RefObject<HTMLInputElement>;
	listRef: React.RefObject<HTMLDivElement>;
	selectedOption: T | undefined;
	handleToggle: () => void;
	handleSelect: (option: T) => void;
	handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleInputKeyDown: (e: React.KeyboardEvent) => void;
}

const useDonateItemDropdown = <T extends { value: string; label: string }>(
	options: T[],
	value: string,
	onChange: (value: string) => void,
): UseDonateItemDropdownReturn<T> => {
	const [isOpen, setIsOpen] = useState(false);
	const [filterText, setFilterText] = useState('');
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);

	const selectedOption = options.find((option) => option.value === value);
	const filteredOptions = options.filter((option) => {
		if (!filterText.trim()) return true;
		const lowerFilter = filterText.toLowerCase();
		return (
			option.value.toLowerCase().includes(lowerFilter) ||
			option.label.toLowerCase().includes(lowerFilter)
		);
	});

	const handleToggle = useCallback(() => {
		setIsOpen((isCurrentlyOpen) => !isCurrentlyOpen);
		if (!isOpen) {
			setTimeout(() => inputRef.current?.focus(), 50);
		}
	}, [isOpen]);

	const handleSelect = useCallback(
		(option: T) => {
			onChange(option.value);
			setIsOpen(false);
			setFilterText('');
		},
		[onChange],
	);

	const handleInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) =>
			setFilterText(event.target.value),
		[],
	);

	const handleInputKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === 'Escape') {
				setIsOpen(false);
				setFilterText('');
			} else if (event.key === 'Enter' && filteredOptions.length > 0) {
				handleSelect(filteredOptions[0]);
			}
		},
		[filteredOptions, handleSelect],
	);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
				setFilterText('');
			}
		};

		let timeoutId: NodeJS.Timeout;
		if (isOpen) {
			timeoutId = setTimeout(() => {
				document.addEventListener('click', handleClickOutside);
			}, 300);
		}

		return () => {
			clearTimeout(timeoutId);
			document.removeEventListener('click', handleClickOutside);
		};
	}, [isOpen]);

	return {
		isOpen,
		filterText,
		filteredOptions,
		containerRef,
		inputRef,
		listRef,
		selectedOption,
		handleToggle,
		handleSelect,
		handleInputChange,
		handleInputKeyDown,
	};
};

export { useDonateItemDropdown };
